import React, { useState } from 'react';
import { Button, Icon } from '@grofers/dock-design-kit';
import { Skeleton } from '../../../ComponentsV1/Skeleton/Skeleton';
import ProductCard from './ProductCard';
import UploadedImages from './UploadedImages';
import VerificationChecklist from './VerificationChecklist';
import ConfirmationModal from './ConfirmationModal';
import {
  MOCK_PRODUCTS,
  MOCK_UPLOADED_IMAGES,
  VERIFICATION_CHECKLIST_ITEMS,
} from '../constants';

const GUIDANCE_STATUS = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/**
 * The checklist + product list + Verify/Cannot Verify actions - the only
 * part that actually depends on `verify_complaint`, so it's the only part
 * that skeletons while that's polling.
 *
 * Each product card also carries its own grievance-level dropdown, sourced
 * from `confirm_complaint`'s per-item `grievance_level` options (Figma node
 * 2328:65422) - selection isn't required to Verify/Cannot Verify, it's just
 * threaded into `/submit_verification`'s per-item `grievance` field.
 *
 * `products` is already expanded into one row per item, or more once the
 * agent has split some of an item's quantity off into its own grievance -
 * up to one row per unit (see CopilotView's `step3Rows`). A row's "-"
 * peels 1 unit into a brand new row (`onSplitRow`, enabled while
 * `row.canSplit`); "+" undoes the most recent split into it, pulling a
 * unit back from whichever row was split off last (`onMergeRow`, enabled
 * while `row.canMerge` - i.e. there's another row to merge from).
 *
 * `checkedIds`/`onCheckedIdsChange` and the row state above are controlled
 * by CopilotView rather than owned here - AccordionStep unmounts this
 * component while its step is collapsed, so local state would reset every
 * time the agent closed step 3 (e.g. to look at step 4) and reopened it.
 */
const VerificationSection = ({
  products,
  verificationSteps,
  guidanceStatus,
  checkedIds,
  onCheckedIdsChange,
  onSplitRow,
  onMergeRow,
  onGrievanceChange,
  onComplete,
  orderId,
  cartId,
  customerId,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (guidanceStatus === GUIDANCE_STATUS.PENDING || !guidanceStatus) {
    return (
      <Skeleton
        className='tw-gap-2'
        rows={[
          { cols: 1, height: '10rem' },
          { cols: 1, height: '5rem' },
          { cols: 1, height: '10rem' },
          { cols: 1, height: '5rem' },
        ]}
      />
    );
  }

  if (guidanceStatus === GUIDANCE_STATUS.FAILED) {
    return (
      <div className='tw-w-full tw-rounded-[12px] tw-bg-[#fef3f2] tw-p-[12px]'>
        <p className='tw-text-[12px] tw-font-regular tw-text-red-500 tw-m-0'>
          Could not fetch verification steps. Please try again.
        </p>
      </div>
    );
  }

  // Verify only lights up once every step is checked; Cannot Verify is the
  // mutually-exclusive counterpart - enabled exactly when Verify isn't
  // (grievance selection doesn't factor into Cannot Verify - it's only a
  // Verify precondition).
  const allStepsChecked =
    verificationSteps.length > 0 &&
    checkedIds.size === verificationSteps.length;

  // Grievance level is required per row, not optional - a row with no
  // options at all (nothing to pick) doesn't block Verify, but one that
  // has options and no selection does.
  const allGrievancesSelected = products.every(
    (row) =>
      (row.grievanceOptions?.length ?? 0) === 0 ||
      Boolean(row.selectedGrievance),
  );

  const canVerify = allStepsChecked && allGrievancesSelected;

  const handleVerify = () => {
    if (!canVerify) return;
    setIsConfirmOpen(true);
  };

  const handleConfirmVerify = () => {
    setIsConfirmOpen(false);
    onComplete({ checkedStepIds: checkedIds, verified: true });
  };

  const handleCannotVerify = () => {
    if (allStepsChecked) return;
    onComplete({ checkedStepIds: checkedIds, verified: false });
  };

  return (
    <>
      <div className='tw-flex tw-flex-col tw-gap-[16px] tw-w-full tw-bg-[#f9fafb] tw-rounded-[12px] tw-p-[12px]'>
        <VerificationChecklist
          items={verificationSteps}
          checkedIds={checkedIds}
          onChange={onCheckedIdsChange}
          orderId={orderId}
          cartId={cartId}
          customerId={customerId}
        />

        <div className='tw-flex tw-gap-3 tw-items-center tw-px-4 tw-py-2 tw-bg-blue-100 tw-rounded-lg'>
          <Icon
            iconColor={{ color: 'blue', weight: '600' }}
            name='exclamation-circle'
            size='md'
          />
          <p className='tw-text-[12px] tw-font-regular tw-leading-tight'>
            If case of any unticked boxes, click on ‘Cannot Verify’
          </p>
        </div>

        <div className='tw-flex tw-flex-col tw-gap-[12px] tw-w-full'>
          <p className='tw-text-[14px] tw-leading-[18px] tw-font-medium tw-text-grey-800 tw-m-0'>
            Select the most suitable condition
          </p>
          <div className='tw-flex tw-flex-col tw-gap-[8px] tw-w-full'>
            {products.map((row) => (
              <div
                key={row.rowKey}
                className='tw-w-full tw-rounded-[8px] tw-overflow-hidden tw-border tw-border-solid tw-border-[#f3f4f6] tw-bg-white'
              >
                <ProductCard
                  imageUrl={row.imageUrl}
                  name={row.name}
                  quantityLabel={row.quantityLabel}
                  count={row.count}
                  maxCount={row.maxCount}
                  onIncrement={() => onMergeRow(row.id, row.rowId)}
                  onDecrement={() => onSplitRow(row.id, row.rowId)}
                  increaseDisabled={!row.canMerge}
                  decreaseDisabled={!row.canSplit}
                  grievanceOptions={row.grievanceOptions}
                  selectedGrievance={row.selectedGrievance}
                  onGrievanceChange={(slug) =>
                    onGrievanceChange(row.rowKey, row.id, slug)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className='tw-flex tw-gap-[12px] tw-w-full'>
        <div className='tw-w-1/2'>
          <Button
            variant='secondary'
            buttonType='default'
            size='md'
            shape='square'
            disabled={allStepsChecked}
            onClick={handleCannotVerify}
            className='tw-w-full'
          >
            Cannot Verify
          </Button>
        </div>
        <div className='tw-w-1/2'>
          <Button
            variant='primary'
            buttonType='default'
            size='md'
            shape='square'
            disabled={!canVerify}
            onClick={handleVerify}
            className={`tw-w-full ${canVerify ? '!tw-bg-base-green' : ''}`}
          >
            Verify
          </Button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        message={
          <div>
            <p className='tw-flex tw-flex-wrap tw-gap-x-1 tw-text-[14px] tw-leading-tight tw-font-regular tw-text-grey-800 tw-m-0 tw-w-full'>
              Are you sure you want to proceed with verifying this complaint?
              <span className='tw-font-semibold tw-text-grey-900 tw-mt-1 tw-text-[15px]'>
                As this would affect the final resolution{' '}
              </span>
            </p>
          </div>
        }
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmVerify}
      />
    </>
  );
};

/**
 * Content of accordion step 3 - "Verification". Phase 1 supports exactly
 * one complaint per order, covering every item `verify_complaint`
 * returned - the agent walks the dynamic checklist against the uploaded
 * images, picks a grievance level, then verifies (or flags unverifiable).
 *
 * The intro text and uploaded-images strip don't depend on
 * `verify_complaint` at all, so they render immediately rather than
 * skeletoning along with the section below.
 *
 * `products`/`images`/`verificationSteps` default to mock data until a
 * live order is walked through - see constants.js.
 */
const Verification = ({
  products = MOCK_PRODUCTS,
  images = MOCK_UPLOADED_IMAGES,
  verificationSteps = VERIFICATION_CHECKLIST_ITEMS,
  guidanceStatus = null,
  checkedIds,
  onCheckedIdsChange = () => {},
  onSplitRow = () => {},
  onMergeRow = () => {},
  onGrievanceChange = () => {},
  onComplete = () => {},
  orderId = null,
  cartId = null,
  customerId = null,
}) => {
  return (
    <div className='tw-flex tw-flex-col tw-gap-[16px] tw-w-full tw-mt-4'>
      <p className='tw-text-[12px] tw-leading-tight tw-font-regular tw-text-grey-800 tw-m-0 tw-w-full'>
        Follow the steps below to verify the complaint with the uploaded images
      </p>

      <UploadedImages
        images={images}
        orderId={orderId}
        cartId={cartId}
        customerId={customerId}
      />

      <VerificationSection
        products={products}
        verificationSteps={verificationSteps}
        guidanceStatus={guidanceStatus}
        checkedIds={checkedIds}
        onCheckedIdsChange={onCheckedIdsChange}
        onSplitRow={onSplitRow}
        onMergeRow={onMergeRow}
        onGrievanceChange={onGrievanceChange}
        onComplete={onComplete}
        orderId={orderId}
        cartId={cartId}
        customerId={customerId}
      />
    </div>
  );
};

export default Verification;
